import { useCallback, useEffect, useState } from 'react'
import { useLangue, useTextes } from '../LangueContexte'
import { dateHeure, jourCourt, montant, nombre } from '../formats'
import { useNavigate } from 'react-router-dom'
import { Vignette } from './Vignette'
import { Bloc, Bouton, EtatVide, Icone, LienBouton, Puce } from '../ui'
import {
  enregistrerRecherche,
  listerFavoris,
  listerNouveautes,
  listerRecherches,
  marquerRechercheVue,
  modifierRecherche,
  retirerFavori,
  supprimerRecherche,
  type PageResultats,
  type RechercheEnregistree,
} from '../apiRecherche'
import { ecrireCriteres, type Criteres, type Frequence } from '../criteres'
import { interpoler, type Cles, type Langue } from '../i18n'

/** Les annonces gardées. */
export function Favoris() {
  const t = useTextes()
  const langue = useLangue()
  const [resultats, setResultats] = useState<PageResultats | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const charger = useCallback(() => {
    void listerFavoris()
      .then(setResultats)
      .catch((cause: Error) => setErreur(cause.message))
  }, [])

  useEffect(charger, [charger])

  const retirer = useCallback(
    async (id: string) => {
      setResultats((precedent) =>
        precedent === null
          ? precedent
          : { ...precedent, items: precedent.items.filter((item) => item.id !== id), total: precedent.total - 1 },
      )

      try {
        await retirerFavori(id)
      } catch {
        charger()
      }
    },
    [charger],
  )

  if (erreur !== null) {
    return <p className="erreur">{erreur}</p>
  }

  return (
    <section className="suivi">
      {/*
        **Le compte, à droite du titre** — ticket `fo-favoris-01`, et c'est ce que montre la
        maquette. « Mes favoris » seul ne dit pas s'il y en a trois ou trente, alors que c'est la
        première chose qu'on vient vérifier ; et sans lui, rien ne distingue une liste vide d'une
        liste qui n'a pas fini de charger.

        Le compte vient de `total` et non de la longueur de la liste : les deux coïncident tant
        que la page tient tout, et le jour où elle ne tiendra plus, c'est le total qui a un sens.

        La phrase sous le titre est là parce que l'écran a maintenant un comportement qui ne se
        devine pas : une annonce grisée est retirée du marché, elle n'a pas disparu.
      */}
      <div className="page-entete">
        <div className="page-entete__rangee">
          <h2>{t.suiviFavoris}</h2>
          {resultats !== null && resultats.total > 0 && (
            <span className="puce">
              {nombre(resultats.total, langue)}{' '}
              {resultats.total > 1 ? t.rechercheAnnonces : t.rechercheAnnonce}
            </span>
          )}
        </div>
        {resultats !== null && resultats.items.some((annonce) => !annonce.isAvailable) && (
          <p>{t.suiviFavorisRetirees}</p>
        )}
      </div>

      {resultats !== null && resultats.items.length === 0 && (
        <EtatVide
          icone="coeur"
          titre={t.suiviFavorisVide}
          action={
            <LienBouton to="/recherche" apparence="secondaire">
              {t.rechercheChercher}
            </LienBouton>
          }
        >
          {t.suiviFavorisVideAide}
        </EtatVide>
      )}

      <ul className="grille-annonces">
        {resultats?.items.map((annonce) => (
          <Vignette key={annonce.id} annonce={annonce} avecVille onFavori={(id) => retirer(id)} />
        ))}
      </ul>
    </section>
  )
}

/** Les trois fréquences. Les libellés viennent d'`i18n`, jamais du tableau (§6, règle 2). */
const FREQUENCES: { valeur: Frequence; libelle: Cles; aide: Cles }[] = [
  { valeur: 'Daily', libelle: 'suiviFrequenceJour', aide: 'suiviFrequenceJourAide' },
  { valeur: 'Instant', libelle: 'suiviFrequenceInstant', aide: 'suiviFrequenceInstantAide' },
  { valeur: 'None', libelle: 'suiviFrequenceAucune', aide: 'suiviFrequenceAucuneAide' },
]

/**
 * Mes recherches — revu le 15/09/2026 sur la maquette `docs/ui/pwa/mes-recherches.html`.
 *
 * <b>Une recherche enregistrée est littéralement une URL</b> (acquis de L3) : « Ouvrir » rend
 * exactement ce qu'on voyait. <b>L'alerte est un interrupteur</b> — la fréquence choisie ou rien —
 * et le basculer ne remet pas le compteur de nouveautés à zéro : seul un changement de critères le
 * fait, côté serveur.
 *
 * <b>Le compteur de nouveautés EST l'alerte</b> tant qu'aucun canal d'envoi n'existe (conception L3,
 * §1.5) : le job relève les correspondances, l'écran les annonce. Promettre un e-mail qui ne
 * partira pas serait la pire des options.
 */
export function MesRecherches() {
  const t = useTextes()
  const langue = useLangue()
  const navigate = useNavigate()
  const [recherches, setRecherches] = useState<RechercheEnregistree[] | null>(null)
  const [ouverte, setOuverte] = useState<string | null>(null)
  const [nouveautes, setNouveautes] = useState<PageResultats | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState<string | null>(null)

  const charger = useCallback(() => {
    void listerRecherches()
      .then(setRecherches)
      .catch((cause: Error) => setErreur(cause.message))
  }, [])

  useEffect(charger, [charger])

  const ouvrir = useCallback(async (recherche: RechercheEnregistree) => {
    setOuverte(recherche.id)
    setNouveautes(await listerNouveautes(recherche.id))
  }, [])

  const agir = async (id: string, action: () => Promise<unknown>) => {
    setOccupe(id)
    setErreur(null)

    try {
      await action()
      charger()
    } catch (cause: unknown) {
      setErreur(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setOccupe(null)
    }
  }

  /** L'alerte bascule entre « rien » et la fréquence choisie — la quotidienne par défaut. */
  const basculerAlerte = (recherche: RechercheEnregistree) =>
    void agir(recherche.id, () =>
      modifierRecherche(
        recherche.id,
        recherche.name,
        recherche.criteria,
        recherche.frequency === 'None' ? 'Daily' : 'None',
      ),
    )

  return (
    <section className="suivi">
      <div className="page-entete">
        <div className="page-entete__rangee">
          <h2>{t.suiviRecherches}</h2>
          <LienBouton to="/recherche" apparence="secondaire" taille="sm">
            <Icone nom="plus" taille="sm" /> {t.suiviNouvelleRecherche}
          </LienBouton>
        </div>
        <p>{t.suiviRecherchesIntro}</p>
      </div>

      {erreur !== null && <p className="erreur">{erreur}</p>}

      {recherches !== null && recherches.length === 0 && (
        <EtatVide
          icone="signet"
          titre={t.suiviRecherchesVide}
          action={
            <LienBouton to="/recherche" apparence="secondaire">
              {t.rechercheChercher}
            </LienBouton>
          }
        >
          {t.suiviRecherchesVideAide}
        </EtatVide>
      )}

      {recherches !== null && recherches.length > 0 && (
        <div className="pile">
          {recherches.map((recherche) => (
            <Bloc
              key={recherche.id}
              titre={
                <div>
                  <h3>{recherche.name}</h3>
                  <p className="secondaire">{resumeCriteres(t, langue, recherche.criteria)}</p>
                </div>
              }
              actions={
                recherche.unseenCount > 0 ? (
                  <Puce ton="marque">{recherche.unseenCount === 1 ? t.suiviUneNouveaute : interpoler(t.suiviNNouveautes, { n: recherche.unseenCount }, langue)}</Puce>
                ) : (
                  <Puce>{t.suiviRienDeNeufCourt}</Puce>
                )
              }
            >
              <div className="rangee rangee--espace">
                <label className="interrupteur">
                  <input
                    type="checkbox"
                    checked={recherche.frequency !== 'None'}
                    disabled={occupe === recherche.id}
                    onChange={() => basculerAlerte(recherche)}
                  />
                  <span className="interrupteur__piste" />
                  <span>{libelleAlerte(t, recherche.frequency)}</span>
                </label>
                <div className="rangee rangee--serree">
                  {recherche.unseenCount > 0 && (
                    <Bouton apparence="secondaire" taille="sm" onClick={() => void ouvrir(recherche)}>
                      {t.suiviVoirNouveautes}
                    </Bouton>
                  )}
                  <Bouton
                    apparence="secondaire"
                    taille="sm"
                    onClick={() => navigate(`/recherche?${ecrireCriteres(recherche.criteria)}`)}
                  >
                    {t.suiviOuvrir}
                  </Bouton>
                  <Bouton
                    apparence="discret"
                    taille="sm"
                    charge={occupe === recherche.id}
                    onClick={() => void agir(recherche.id, () => supprimerRecherche(recherche.id))}
                  >
                    <Icone nom="poubelle" taille="sm" /> {t.suiviSupprimer}
                  </Bouton>
                </div>
              </div>

              <p className="tertiaire suivi__execution">
                {recherche.lastRunAt === null
                  ? t.suiviJamaisExecutee
                  : interpoler(t.suiviDerniereExecution, { date: dateHeure(recherche.lastRunAt, langue) })}
              </p>

              {ouverte === recherche.id && nouveautes !== null && (
                <div className="suivi__nouveautes">
                  <div className="rangee rangee--espace">
                    <h4>{t.suiviNouveautes}</h4>
                    {recherche.unseenCount > 0 && (
                      <Bouton apparence="discret" taille="sm" onClick={() => void agir(recherche.id, () => marquerRechercheVue(recherche.id))}>
                        {t.suiviToutVu}
                      </Bouton>
                    )}
                  </div>
                  {nouveautes.items.length === 0 ? (
                    <p className="secondaire">{t.suiviRienDeNeuf}</p>
                  ) : (
                    <ul className="grille-annonces">
                      {nouveautes.items.map((annonce) => (
                        <Vignette key={annonce.id} annonce={annonce} avecVille />
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </Bloc>
          ))}
        </div>
      )}
    </section>
  )
}

/** « Chambre privée · Al Wifaq · meublé · jusqu'à 2 500 MAD » — ce que la recherche demande, en une ligne. */
function resumeCriteres(t: ReturnType<typeof useTextes>, langue: Langue, criteres: Criteres): string {
  const morceaux: string[] = []

  // Le serveur rend les critères absents à `null`, l'URL les omet : les deux valent « pas de filtre ».
  if (criteres.unitKind === 'PrivateRoom') morceaux.push(t.typeChambrePrivee)
  else if (criteres.unitKind === 'SharedRoom') morceaux.push(t.typeChambrePartagee)
  else if (criteres.unitKind === 'EntireProperty') morceaux.push(t.typeLogementEntier)
  if (criteres.district != null && criteres.district !== '') morceaux.push(criteres.district)
  if (criteres.furnished === true) morceaux.push(t.publicationMeuble)
  if (criteres.minBedrooms != null) morceaux.push(criteres.minBedrooms === 1 ? t.suiviUneChambre : interpoler(t.suiviNChambres, { n: criteres.minBedrooms }, langue))
  if (criteres.maxRent != null) morceaux.push(interpoler(t.suiviJusquA, { montant: montant(criteres.maxRent, 'MAD', langue) }))
  if (criteres.availableFrom != null) morceaux.push(interpoler(t.suiviDisponibleAvant, { date: jourCourt(criteres.availableFrom, langue) }))
  if (criteres.text != null && criteres.text !== '') morceaux.push(`« ${criteres.text} »`)

  return morceaux.length === 0 ? t.suiviTousCriteres : morceaux.join(' · ')
}

function libelleAlerte(t: ReturnType<typeof useTextes>, frequence: Frequence): string {
  switch (frequence) {
    case 'Daily':
      return t.suiviAlerteQuotidienne
    case 'Instant':
      return t.suiviAlerteInstantanee
    default:
      return t.suiviAlerteAucune
  }
}

/**
 * Enregistre la recherche courante.
 *
 * Affiché depuis l'écran de recherche. Le nom est libre et pré-rempli : demander de le saisir avant
 * d'avoir vu les résultats est le meilleur moyen de n'en faire enregistrer aucune.
 */
export function EnregistrerRecherche({
  criteres,
  suggestion,
  onEnregistree,
  desactive = false,
  raisonDesactive,
}: {
  criteres: Criteres
  suggestion: string
  onEnregistree: () => void
  /** Grisé quand il n'y a rien à enregistrer — l'appelant décide, voir `fo-recherche-10`. */
  desactive?: boolean
  raisonDesactive?: string
}) {
  const t = useTextes()
  const [ouvert, setOuvert] = useState(false)
  const [nom, setNom] = useState(suggestion)
  const [frequence, setFrequence] = useState<Frequence>('Daily')
  const [erreur, setErreur] = useState<string | null>(null)

  if (!ouvert) {
    // Une puce et non un lien : elle voisine le bouton de carte dans l'en-tête de résultats, et
    // deux contrôles côte à côte qui ne se ressemblent pas se lisent comme deux natures de
    // choses. C'est aussi ce que montre la maquette (ticket `fo-recherche-05`).
    return (
      <button
        type="button"
        className="puce puce--action"
        disabled={desactive}
        title={desactive ? raisonDesactive : undefined}
        onClick={() => setOuvert(true)}
      >
        <Icone nom="signet" taille="sm" />
        {t.suiviEnregistrerRecherche}
      </button>
    )
  }

  return (
    <form
      className="enregistrer-recherche"
      onSubmit={async (evenement) => {
        evenement.preventDefault()
        setErreur(null)

        try {
          await enregistrerRecherche(nom, criteres, frequence)
          setOuvert(false)
          onEnregistree()
        } catch (cause) {
          setErreur((cause as Error).message)
        }
      }}
    >
      <label>
        {t.suiviNomDeLaRecherche}
        <input value={nom} onChange={(evenement) => setNom(evenement.target.value)} maxLength={80} required />
      </label>

      <fieldset>
        <legend>{t.suiviNouveautes}</legend>
        {FREQUENCES.map((option) => (
          <label key={option.valeur} className="radio">
            <input
              type="radio"
              name="frequence"
              value={option.valeur}
              checked={frequence === option.valeur}
              onChange={() => setFrequence(option.valeur)}
            />
            {t[option.libelle]}
            <small>{t[option.aide]}</small>
          </label>
        ))}
      </fieldset>

      <p className="enregistrer-recherche__note">
        {t.suiviPasDEnvoi}
      </p>

      {erreur !== null && <p className="erreur">{erreur}</p>}

      <div className="enregistrer-recherche__actions">
        <button type="submit">{t.suiviEnregistrer}</button>
        <button type="button" className="lien" onClick={() => setOuvert(false)}>
          {t.annuler}
        </button>
      </div>
    </form>
  )
}
